import { useSyncExternalStore } from 'react'
import { invariant } from '../lib/invariant'
import { FileHandlesStore } from './FileHandlesStore'
import {
  ProjectFile,
  ProjectFolder,
  ProjectItem,
  ProjectPath,
  ProjectStore,
  areProjectItemsEqual,
  findProjectItemWithPath,
  getProjectItemParentFolder,
  isProjectFolder,
  moveProjectItem,
  nameForProposedName,
  removeProjectItem,
  renameProjectItem,
  visitProjectItems
} from './ProjectItem'
import {
  createDirectory,
  createFile,
  isDirectoryHandle,
  isFileHandle,
  moveDirectory,
  moveFile,
  removeEntry,
  renameDirectory,
  renameFile,
  verifyPermission,
  visitHandles
} from './fs'

function projectPathFromHandlePath(handlePath: string, rootProjectPath: ProjectPath): ProjectPath {
  const deliminatorIndex = handlePath.indexOf('/')
  if (deliminatorIndex >= 0) {
    return `${rootProjectPath}${handlePath.slice(deliminatorIndex)}`
  } else {
    return rootProjectPath
  }
}

const serverSnapshot: ProjectItem[] = []

export function useCurrentProjectItems() {
  return useSyncExternalStore(
    (onStoreChange) => {
      ProjectManager.shared().addListener(onStoreChange)
      return () => ProjectManager.shared().removeListener(onStoreChange)
    },
    () => {
      return ProjectManager.shared().getCurrentItems()
    },
    () => serverSnapshot
  )
}

let sharedProjectManager: ProjectManager

export class ProjectManager {
  private constructor(private readonly fileHandlesStore: FileHandlesStore) {}

  static shared() {
    if (!sharedProjectManager) {
      const fileHandlesStore = new FileHandlesStore()
      sharedProjectManager = new ProjectManager(fileHandlesStore)
      sharedProjectManager.reloadItems()
    }
    return sharedProjectManager
  }

  // Items

  private currentItems: ProjectItem[] = []

  getCurrentItems() {
    return this.currentItems
  }

  findItemWithPath(path: string) {
    return findProjectItemWithPath(this.currentItems, path)
  }

  // Actions

  createFileSync(
    parentFolder: ProjectFolder | null,
    proposedName: string,
    initialText: string,
    onCreate: (createdFile: ProjectFile) => void
  ) {
    const store = parentFolder?.store ?? 'originPrivateFileSystem'
    const name = nameForProposedName(this.currentItems, proposedName, parentFolder)
    const path: ProjectPath = `${parentFolder?.path ?? ''}/${name}`
    const newFile: ProjectFile = {
      store,
      name,
      path,
      fileHandle: null
    }
    if (parentFolder) {
      parentFolder.items.push(newFile)
      parentFolder.items.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      this.currentItems = [...this.currentItems] // ensure Object.is on snapshot returns false
    } else {
      this.currentItems = [...this.currentItems, newFile]
      this.currentItems.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    }

    // create on file system in background
    ;(async () => {
      try {
        const parentDirectoryHandle = parentFolder
          ? parentFolder.directoryHandle
          : await navigator.storage.getDirectory()
        invariant(parentDirectoryHandle, `Parent directory does not exist '${parentFolder?.path ?? ''}'`)

        await createFile(parentDirectoryHandle, newFile.name, initialText)
      } catch (error) {
        console.error(error)
      } finally {
        await this.reloadItems()
      }
    })()

    this.dispatchChangeEvent()

    onCreate(newFile)
  }

  createFolderSync(
    parentFolder: ProjectFolder | null,
    proposedName: string,
    onCreate: (createdFolder: ProjectFolder) => void
  ) {
    const store = parentFolder?.store ?? 'originPrivateFileSystem'
    const name = nameForProposedName(this.currentItems, proposedName, parentFolder)
    const path: ProjectPath = `${parentFolder?.path ?? ''}/${name}`
    const newFolder: ProjectFolder = {
      store,
      name,
      path,
      items: [],
      directoryHandle: null
    }
    if (parentFolder) {
      parentFolder.items.push(newFolder)
      parentFolder.items.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      this.currentItems = [...this.currentItems] // ensure Object.is on snapshot returns false
    } else {
      this.currentItems = [...this.currentItems, newFolder]
      this.currentItems.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    }

    // create on file system in background
    ;(async () => {
      try {
        const parentDirectoryHandle = parentFolder
          ? parentFolder.directoryHandle
          : await navigator.storage.getDirectory()
        invariant(parentDirectoryHandle, `Parent directory does not exist '${parentFolder?.path ?? ''}'`)

        await createDirectory(parentDirectoryHandle, newFolder.name)
      } catch (error) {
        console.error(error)
      } finally {
        await this.reloadItems()
      }
    })()

    this.dispatchChangeEvent()

    onCreate(newFolder)
  }

  async openFile() {
    try {
      const openedFileHandles = await window.showOpenFilePicker({
        types: [
          {
            accept: {
              'text/plain': ['.txt', '.md'],
              'application/json': ['.json'],
              'application/yaml': ['.yaml', '.yml']
            }
          }
        ]
      })

      let existingFileHandles: FileSystemFileHandle[] = []
      visitProjectItems(this.currentItems, (item, parentFolder) => {
        if (!isProjectFolder(item) && item.fileHandle) {
          existingFileHandles.push(item.fileHandle)
        }
        return false
      })

      for (const openedFileHandle of openedFileHandles) {
        for (const existingFileHandle of existingFileHandles) {
          if (await existingFileHandle.isSameEntry(openedFileHandle)) {
            continue // already open
          }
        }

        // Open File always adds to top level (unless already open)
        const name = nameForProposedName(this.currentItems, openedFileHandle.name, null)
        const path: ProjectPath = `/${name}`

        await this.fileHandlesStore.addHandle(openedFileHandle, path)
      }

      // Resync with file system
      await this.reloadItems()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
      throw error
    }
  }

  async openFolder() {
    return await this.addFolderToWorkspace()
  }

  async addFolderToWorkspace() {
    try {
      const openedDirectoryHandle = await window.showDirectoryPicker()
      if (!(await verifyPermission(openedDirectoryHandle, 'readwrite'))) {
        return
      }

      let existingFolders: ProjectFolder[] = []
      visitProjectItems(this.currentItems, (item, parentFolder) => {
        if (isProjectFolder(item)) {
          existingFolders.push(item)
        }
        return false
      })
      for (const existingFolder of existingFolders) {
        if (
          existingFolder.directoryHandle &&
          (await existingFolder.directoryHandle.isSameEntry(openedDirectoryHandle))
        ) {
          return // already open
        }
      }

      // Open Folder always adds to top level (unless already open)
      const name = nameForProposedName(this.currentItems, openedDirectoryHandle.name, null)
      const path: ProjectPath = `/${name}`

      await this.fileHandlesStore.addHandle(openedDirectoryHandle, path)

      // resync with file system
      await this.reloadItems()
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
      throw error
    }
  }

  canSaveFile(file: ProjectFile) {
    return file.fileHandle ? true : false
  }

  // TODO: get text from document?
  async saveFile(file: ProjectFile, text: string) {
    const fileHandle = file.fileHandle
    if (fileHandle) {
      const writable = await fileHandle.createWritable()
      await writable.truncate(0)
      await writable.write(text)
      await writable.close()
    }
  }

  // TODO: get text from document?
  // TODO: how to get saved location?
  async saveFileAs(file: ProjectFile, text: string) {
    try {
      const fileHandle = await window.showSaveFilePicker({ suggestedName: file.name })

      if (fileHandle) {
        const writable = await fileHandle.createWritable()
        await writable.truncate(0)
        await writable.write(text)
        await writable.close()

        // TODO: will it show up in navigator if saved outside top level directory?
        await this.reloadItems()
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return
      }
      throw error
    }
  }

  renameItemSync(item: ProjectItem, proposedNewName: string, onRename: (renamedItem: ProjectItem) => void) {
    const parentFolder = getProjectItemParentFolder(this.currentItems, item)
    const newName = nameForProposedName(this.currentItems, proposedNewName, parentFolder)
    const newPath: ProjectPath = `${parentFolder?.path ?? ''}/${newName}`

    // rename items synchronously
    this.currentItems = renameProjectItem(this.currentItems, item, newName)

    // rename on file system in background
    ;(async () => {
      // TODO: need to check whether top level user file/folder is being renamed??
      try {
        const parentDirectoryHandle = parentFolder
          ? parentFolder.directoryHandle
          : await navigator.storage.getDirectory()
        invariant(parentDirectoryHandle, `Parent directory does not exist '${parentFolder?.path ?? ''}'`)

        if (isProjectFolder(item)) {
          invariant(item.directoryHandle)
          await renameDirectory(item.directoryHandle, parentDirectoryHandle, newName)
        } else {
          invariant(item.fileHandle)
          await renameFile(item.fileHandle, parentDirectoryHandle, newName)
        }
      } catch (error) {
        console.error(error)
      } finally {
        await this.reloadItems()
      }
    })()

    this.dispatchChangeEvent()

    const renamedItem = this.findItemWithPath(newPath)
    if (renamedItem) {
      onRename(renamedItem)
    }
  }

  moveItemsSync(items: ProjectItem[], newParentFolder: ProjectFolder | null) {
    for (const item of items) {
      this.moveItemSync(item, newParentFolder)
    }
  }

  moveItemSync(item: ProjectItem, newParentFolder: ProjectFolder | null) {
    if (item.store === 'userFileSystem') {
      return // cannot move user files or folders
    }

    const oldParentFolder = getProjectItemParentFolder(this.currentItems, item)
    if (oldParentFolder === newParentFolder) {
      return // no-op
    }

    const newName = nameForProposedName(this.currentItems, item.name, newParentFolder)

    // move item synchronously
    this.currentItems = moveProjectItem(this.currentItems, item, newParentFolder, newName)

    // move on file system in background
    ;(async () => {
      // TODO: need to check whether top level user file/folder is being moved??
      try {
        const oldParentDirectoryHandle = oldParentFolder
          ? oldParentFolder.directoryHandle
          : await navigator.storage.getDirectory()
        invariant(oldParentDirectoryHandle, `Parent directory does not exist '${oldParentFolder?.path ?? ''}'`)

        const newParentDirectoryHandle = newParentFolder
          ? newParentFolder.directoryHandle
          : await navigator.storage.getDirectory()
        invariant(newParentDirectoryHandle)

        if (isProjectFolder(item)) {
          invariant(item.directoryHandle)
          await moveDirectory(item.directoryHandle, oldParentDirectoryHandle, newParentDirectoryHandle)
        } else {
          invariant(item.fileHandle)
          await moveFile(item.fileHandle, oldParentDirectoryHandle, newParentDirectoryHandle)
        }
      } catch (error) {
        console.error(error)
      } finally {
        await this.reloadItems()
      }
    })()

    this.dispatchChangeEvent()
  }

  removeItemSync(item: ProjectItem) {
    // remove item synchronously
    this.currentItems = removeProjectItem(this.currentItems, item)

    // remove on file system in background
    ;(async () => {
      // TODO: need to check whether top level user file/folder is being removed?
      try {
        if (item.store === 'originPrivateFileSystem') {
          const parentFolder = getProjectItemParentFolder(this.currentItems, item)
          const parentHandle = parentFolder ? parentFolder.directoryHandle : await navigator.storage.getDirectory()
          invariant(parentHandle)

          const handle = isProjectFolder(item) ? item.directoryHandle : item.fileHandle
          invariant(handle)

          await removeEntry(handle, parentHandle, { recursive: true })
        } else if (item.store === 'userFileSystem') {
          // Remove from IDB, but not disk
          await this.fileHandlesStore.removeHandle(item.path)
        }
      } catch (error) {
        console.error(error)
      } finally {
        await this.reloadItems()
      }
    })()

    this.dispatchChangeEvent()
  }

  async getDirectory(directoryPath: string): Promise<FileSystemDirectoryHandle> {
    const root = await navigator.storage.getDirectory()
    if (directoryPath === '') {
      return root
    }
    if (!directoryPath.includes('/')) {
      return await root.getDirectoryHandle(directoryPath, { create: true })
    }
    const parentPath = directoryPath.slice(0, directoryPath.lastIndexOf('/'))
    const name = directoryPath.slice(directoryPath.lastIndexOf('/') + 1)
    const parent = await this.getDirectory(parentPath)
    return await parent.getDirectoryHandle(name, { create: true })
  }

  async replaceAll(allFiles: Record<string, string>) {
    const root = await navigator.storage.getDirectory()
    for await (const child of root.keys()) {
      await root.removeEntry(child, { recursive: true })
    }

    // sort by path length to avoid creating children before parent
    for (const [path, text] of Object.entries(allFiles)) {
      if (path === '') {
        continue
      }
      const directoryPath = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : ''
      const name = path.includes('/') ? path.slice(path.lastIndexOf('/') + 1) : path
      const parent = await this.getDirectory(directoryPath)
      const file = await parent.getFileHandle(name, { create: true })

      const writable = await file.createWritable()
      await writable.truncate(0)
      await writable.write(text)
      await writable.close()
    }

    await this.reloadItems()
  }

  getNearestFolder(targetPath: ProjectPath | null) {
    if (!targetPath) {
      return null
    }
    const targetItem = this.findItemWithPath(targetPath)
    if (targetItem && isProjectFolder(targetItem)) {
      return targetItem
    }
    if (!targetPath.includes('/')) {
      return null
    }
    const parentPath = targetPath.slice(0, targetPath.lastIndexOf('/'))
    const parentItem = this.findItemWithPath(parentPath)
    if (parentItem) {
      invariant(isProjectFolder(parentItem))
      return parentItem
    }
    return null
  }

  // Handles

  async visitOriginPrivateFileSystemHandles(
    visitor: (projectPath: ProjectPath, handle: FileSystemHandle) => Promise<boolean | undefined>
  ) {
    const rootDirectory = await navigator.storage.getDirectory()
    const stop = await visitHandles(rootDirectory, async (path, handle) => {
      if (path === '') {
        return // skip OPFS root directory
      }
      // for OPFS, handle path is same as project path
      const projectPath = path as ProjectPath
      return await visitor(projectPath, handle)
    })
    if (stop) {
      return true
    }
  }

  async reloadItems() {
    const items: ProjectItem[] = []

    const opfsRootDirectory = await navigator.storage.getDirectory()
    for await (const opfsHandle of opfsRootDirectory.values()) {
      const item = await this.itemFromTopLevelHandle(
        opfsHandle,
        opfsHandle.name,
        `/${opfsHandle.name}`,
        'originPrivateFileSystem'
      )
      items.push(item)
    }

    const topLevelUserFileSystemHandles = await this.fileHandlesStore.entries()
    for await (const [projectPath, handle] of topLevelUserFileSystemHandles) {
      const projectName = projectPath.slice(1)
      invariant(!projectName.includes('/'))
      const item = await this.itemFromTopLevelHandle(handle, projectName, projectPath, 'userFileSystem')
      items.push(item)
    }

    items.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))

    if (!(await areProjectItemsEqual(this.currentItems, items))) {
      this.currentItems = items
      this.dispatchChangeEvent()
    }

    // TODO: watch file system handles in web worker
    // https://github.com/whatwg/fs/blob/main/proposals/FileSystemObserver.md
  }

  private async itemFromTopLevelHandle(
    topLevelHandle: FileSystemHandle,
    topLevelProjectName: string,
    topLevelProjectPath: ProjectPath,
    store: ProjectStore
  ) {
    let topLevelItem: ProjectItem | undefined

    // keyed by file handle path, not project path
    const foldersByHandlePath: Record<string, ProjectFolder> = {}

    await visitHandles(topLevelHandle, async (visitedPath, visitedHandle) => {
      const isTopLevel = !visitedPath.includes('/')

      let item: ProjectItem
      if (isFileHandle(visitedHandle)) {
        const file: ProjectFile = {
          store,
          name: isTopLevel ? topLevelProjectName : visitedHandle.name,
          path: projectPathFromHandlePath(visitedPath, topLevelProjectPath),
          fileHandle: visitedHandle
        }
        item = file
      } else if (isDirectoryHandle(visitedHandle)) {
        const folder: ProjectFolder = {
          store,
          name: isTopLevel ? topLevelProjectName : visitedHandle.name,
          path: projectPathFromHandlePath(visitedPath, topLevelProjectPath),
          items: [],
          directoryHandle: visitedHandle
        }
        foldersByHandlePath[visitedPath] = folder
        item = folder
      } else {
        invariant(false, `Invalid file system handle ${visitedHandle}`)
      }

      if (isTopLevel) {
        topLevelItem = item
      } else {
        const parentPath = visitedPath.slice(0, visitedPath.lastIndexOf('/'))
        const parentFolder = foldersByHandlePath[parentPath]
        invariant(parentFolder)
        parentFolder.items = [...parentFolder.items, item]
        parentFolder.items.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
      }

      return undefined
    })

    invariant(topLevelItem)
    return topLevelItem
  }

  // Listeners

  private listeners = new Set<() => void>()

  addListener(listener: () => void) {
    this.listeners.add(listener)
  }

  removeListener(listener: () => void) {
    this.listeners.delete(listener)
  }

  dispatchChangeEvent() {
    this.listeners.forEach((listener) => listener())
  }
}
